'use client';

import NextLink from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import type { ComponentProps, ReactNode } from 'react';

type LinkProps = Omit<ComponentProps<typeof NextLink>, 'href'> & {
  to: string;
  children?: ReactNode;
  prefetch?: boolean;
};

export function Link({ to, children, prefetch = false, ...rest }: LinkProps) {
  return (
    <NextLink href={to} prefetch={prefetch} {...rest}>
      {children}
    </NextLink>
  );
}

type NavLinkProps = LinkProps & {
  end?: boolean;
  className?: string | ((props: { isActive: boolean }) => string);
};

export function NavLink({ to, end, className, children, ...rest }: NavLinkProps) {
  const pathname = usePathname() ?? '/';
  const normalizedTo = to.replace(/\/$/, '') || '/';
  const normalizedPath = pathname.replace(/\/$/, '') || '/';
  const isActive = end ? normalizedPath === normalizedTo : normalizedPath === normalizedTo || normalizedPath.startsWith(`${normalizedTo}/`);

  const resolvedClassName =
    typeof className === 'function' ? (className as (props: { isActive: boolean }) => string)({ isActive }) : className;

  return (
    <NextLink href={to} className={resolvedClassName} aria-current={isActive ? 'page' : undefined} {...rest}>
      {children}
    </NextLink>
  );
}

export function useLocation() {
  const pathname = usePathname() ?? '/';
  return {
    pathname,
    search: '',
    hash: '',
    state: null,
    key: 'default',
  };
}

type NavigateProps = {
  to: string;
  replace?: boolean;
};

export function Navigate({ to, replace }: NavigateProps) {
  const router = useRouter();
  if (typeof window !== 'undefined') {
    if (replace) router.replace(to);
    else router.push(to);
  }
  return null;
}

// Compatibility stubs (unused in Next.js app)
export function Routes(_props: { children?: ReactNode }) {
  return null;
}

export function Route(_props: { path?: string; element?: ReactNode; children?: ReactNode }) {
  return null;
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export function Outlet() {
  return null;
}
